import { LinkValue } from '@/lib/sanity/portable-text/marks/link.mark';
import type { NavItemVariant } from '@/lib/sanity/dto';

/**
 * Section widths as stored in the CMS (`sectionBlock` → `maxWidth`, pixel values)
 * mapped onto the container scale. Only `SectionBlock` reads it; once the stored
 * values are migrated to step names, this map goes (AGENTS.md → Design contract).
 */
export const PAGE_WIDTH = {
    fullscreen: 'max-w-none',
    '1920': 'max-w-page-xl',
    '1640': 'max-w-page-lg',
    '1400': 'max-w-page-md',
    '1200': 'max-w-page-sm',
    '992': 'max-w-page-xs',
    '768': 'max-w-page-2xs',
    '576': 'max-w-page-3xs',
} satisfies Record<string, string>;

/**
 * Section background image. A CSS `background-image` has no srcset, so the two
 * variants built by `sanityImageSectionStyle` are swapped by breakpoint — only
 * the one the matching rule uses is ever fetched.
 */
export const SECTION_BACKGROUND = 'bg-(image:--bg-image) lg:bg-(image:--bg-image-lg) bg-cover bg-center';

export const SPACING_TOP = {
    '2xl': 'pt-10 lg:pt-36',
    xl: 'pt-9 lg:pt-28',
    lg: 'pt-8 lg:pt-24',
    md: 'pt-7 lg:pt-18',
    sm: 'pt-5 lg:pt-12',
    xs: 'pt-3 lg:pt-9',
    '': '',
} satisfies Record<string, string>;

export const SPACING_BOTTOM = {
    '2xl': 'pb-10 lg:pb-36',
    xl: 'pb-9 lg:pb-28',
    lg: 'pb-8 lg:pb-24',
    md: 'pb-7 lg:pb-18',
    sm: 'pb-5 lg:pb-12',
    xs: 'pb-3 lg:pb-9',
    '': '',
} satisfies Record<string, string>;

export const COLUMN_GAP = {
    xl: 'gap-9 lg:gap-24',
    lg: 'gap-6 lg:gap-24',
    md: 'gap-5 lg:gap-18',
    sm: 'gap-4 lg:gap-12',
    xs: 'gap-3 lg:gap-9',
    '': '',
} satisfies Record<string, string>;

export const GRID_GUTTER = {
    left: 'px-gutter lg:pr-0',
    right: 'px-gutter lg:pl-0',
    both: 'px-gutter',
    full: 'p-gutter',
    slider: 'pl-gutter lg:pr-gutter',
} satisfies Record<string, string>;

export const BACKGROUND_COLOR = {
    Primary: 'bg-primary',
    Secondary: 'bg-body',
} satisfies Record<string, string>;

export const COLUMN_ORDER = {
    1: 'order-1',
    2: 'order-2',
    3: 'order-3',
    4: 'order-4',
    5: 'order-5',
    6: 'order-6',
} satisfies Record<string | number, string>;

export const COLUMN_WIDTH = {
    1: '',
    2: 'lg:col-span-2',
    3: 'lg:col-span-3',
    4: 'lg:col-span-4',
    5: 'lg:col-span-5',
    6: 'lg:col-span-6',
} satisfies Record<string | number, string>;

export const ICON_COLOR_MAP: Record<string, string> = {
    'bag-shopping': 'text-yellow-400',
    binoculars: 'text-pink-400',
    'check-mark-circle': 'text-primary-500',
    'briefcase-medical': 'text-red-500',
    'graduation-cap': 'text-purple-400',
    'plate-utensils': 'text-orange-400',
    route: 'text-bluelight-400',
    'sneaker-running': 'text-teal-400',
    trees: 'text-green-400',
} as const;

export const LINK_VARIANT_MAP: Record<NonNullable<LinkValue['variant']>, string> = {
    'pt-text-link': 'underline underline-offset-2 hover:text-primary',
    'pt-btn-link': 'btn btn-link-primary btn-lg',
    'pt-btn-primary': 'btn btn-primary btn-lg mt-2 lg:mt-0',
    'pt-btn-secondary': 'btn btn-primary-outline btn-lg mt-2 lg:mt-0',
};

/**
 * Render style of a meta-navigation item, chosen per item in the Studio
 * (`navItem.variant`). The classes are the white variants, because the meta
 * navigation only ever sits on the dark header surface.
 */
export const META_NAV_VARIANT_MAP: Record<NavItemVariant, string> = {
    'btn-link': 'btn btn-link-white',
    'link-text':
        'inline-flex items-center whitespace-nowrap font-sans text-xs font-light text-(color:--text-body) transition-colors duration-150 hover:text-white',
};

export const META_NAV_VARIANT_DEFAULT: NavItemVariant = 'btn-link';
